import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { MoneyInput } from '../ui/Input'
import { fmt, fmtS, dateStr, pct, uid } from '../../utils/formatters'
import { exportInvoicesToExcel } from '../../utils/exportExcel'
import { exportInvoiceToPDF } from '../../utils/exportPDF'
import { SkeletonStats, SkeletonList } from '../ui/Skeleton'

const STATUS_LABELS = { draft: 'Draft', sent: 'Terkirim', paid: 'Lunas', overdue: 'Jatuh Tempo' }
const STATUS_BADGES = { draft: 'badge-info', sent: 'badge-accent', paid: 'badge-success', overdue: 'badge-danger' }

export default function Invoices() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [invoices, setInvoices] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [edit, setEdit] = useState(null)
  const [filterProject, setFilterProject] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')

  const blank = {
    project_id: '', term_number: 1, description: '',
    amount: 0, date: new Date().toISOString().split('T')[0],
    due_date: '', status: 'draft'
  }
  const [form, setForm] = useState(blank)

  useEffect(() => { if (!user) return; fetchData() }, [user])

  const fetchData = async () => {
    setLoading(true)
    const [{ data: inv }, { data: proj }] = await Promise.all([
      supabase.from('invoices').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      supabase.from('projects').select('*').eq('user_id', user.id),
    ])
    if (inv) setInvoices(inv)
    if (proj) setProjects(proj)
    setLoading(false)
  }

  const save = async () => {
    if (!form.description) { toast.show('Deskripsi wajib diisi', 'error'); return }
    const data = { ...form, amount: parseInt(form.amount), term_number: parseInt(form.term_number), user_id: user.id }
    if (edit) {
      await supabase.from('invoices').update(data).eq('id', edit)
    } else {
      await supabase.from('invoices').insert([{ ...data, id: uid() }])
    }
    setShow(false)
    toast.show(edit ? 'Invoice diperbarui' : 'Invoice dibuat')
    fetchData()
  }

  const del = async (id, desc) => {
    const ok = await confirm(`Hapus invoice "${desc}"?`, {
      title: 'Hapus Invoice', icon: '💳', confirmLabel: 'Hapus', confirmVariant: 'danger'
    })
    if (!ok) return
    await supabase.from('invoices').delete().eq('id', id)
    toast.show('Invoice dihapus')
    fetchData()
  }

  const markStatus = async (id, status) => {
    await supabase.from('invoices').update({ status }).eq('id', id)
    toast.show(status === 'paid' ? '✅ Invoice dilunasi!' : 'Status diperbarui')
    fetchData()
  }

  const filteredInvoices = invoices.filter(i => {
    const matchP = filterProject === 'all' || i.project_id === filterProject
    const matchS = filterStatus === 'all' || i.status === filterStatus
    return matchP && matchS
  })

  const total = filteredInvoices.reduce((s, i) => s + (i.amount || 0), 0)
  const paid = filteredInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0)
  const unpaid = filteredInvoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.amount || 0), 0)
  const overdueCount = filteredInvoices.filter(i => i.status === 'overdue').length

  if (loading) return <div><SkeletonStats cols={4} /><SkeletonList count={3} /></div>

  return (
    <div>
      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 14 }}>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent)' }}>{fmtS(total)}</div>
          <div className="stat-label">Total Tagihan</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--success)' }}>{fmtS(paid)}</div>
          <div className="stat-label">Sudah Dibayar</div>
          <div className="stat-change" style={{ color: 'var(--success)' }}>{pct(paid, total)}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: unpaid > 0 ? 'var(--warning)' : 'var(--success)' }}>{fmtS(unpaid)}</div>
          <div className="stat-label">Belum Dibayar</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: overdueCount > 0 ? 'var(--danger)' : 'var(--text-primary)' }}>
            {overdueCount}
          </div>
          <div className="stat-label">Jatuh Tempo</div>
        </div>
      </div>

      {/* Overdue alert */}
      {overdueCount > 0 && (
        <div style={{
          background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-lg)',
          padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span>⚠️</span>
          <span style={{ flex: 1, fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>
            {overdueCount} invoice jatuh tempo — segera tindak lanjut!
          </span>
        </div>
      )}

      {/* Filter */}
      <div className="filter-row">
        <select className="form-select" style={{ width: 180 }} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="all">Semua Proyek</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="form-select" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">Semua Status</option>
          <option value="draft">Draft</option>
          <option value="sent">Terkirim</option>
          <option value="paid">Lunas</option>
          <option value="overdue">Jatuh Tempo</option>
        </select>
        <Button variant="secondary" onClick={() => { exportInvoicesToExcel(filteredInvoices, projects); toast.show('Excel diunduh') }}>📊 Excel</Button>
        <Button onClick={() => {
          const pi = invoices.filter(i => i.project_id === (filterProject !== 'all' ? filterProject : projects[0]?.id))
          setForm({ ...blank, term_number: pi.length ? Math.max(...pi.map(i => i.term_number || 0)) + 1 : 1, project_id: filterProject !== 'all' ? filterProject : (projects[0]?.id || '') })
          setEdit(null); setShow(true)
        }}>➕ Invoice</Button>
      </div>

      {/* List */}
      {filteredInvoices.map(inv => {
        const proj = projects.find(p => p.id === inv.project_id)
        const isOverdue = inv.status === 'overdue'
        return (
          <div key={inv.id} className="mobile-card-item" style={{ borderColor: isOverdue ? 'var(--danger)' : undefined }}>
            <div className="mc-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--accent)', fontWeight: 800, fontSize: 14 }}>T{inv.term_number}</span>
                  <span className="mc-title">{inv.description}</span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{proj?.name || '-'}</div>
              </div>
              <span className={`badge ${STATUS_BADGES[inv.status]}`}>{STATUS_LABELS[inv.status]}</span>
            </div>
            <div className="mc-row">
              <span className="mc-label">Nilai</span>
              <span className="mc-value" style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 14 }}>{fmt(inv.amount)}</span>
            </div>
            <div className="mc-row">
              <span className="mc-label">Tanggal</span>
              <span className="mc-value">{dateStr(inv.date)}</span>
            </div>
            <div className="mc-row">
              <span className="mc-label">Jatuh Tempo</span>
              <span className="mc-value" style={{ color: isOverdue ? 'var(--danger)' : undefined }}>{dateStr(inv.due_date) || '-'}</span>
            </div>
            <div className="mc-actions">
              {inv.status !== 'paid' && (
                <Button size="sm" variant="success" onClick={() => markStatus(inv.id, 'paid')}>✅ Lunas</Button>
              )}
              {inv.status === 'draft' && (
                <Button size="sm" variant="info" onClick={() => markStatus(inv.id, 'sent')}>📤 Kirim</Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => { const proj = projects.find(p => p.id === inv.project_id); exportInvoiceToPDF(inv, proj); toast.show('PDF diunduh') }}>📄</Button>
              <Button size="sm" variant="secondary" onClick={() => { setForm(inv); setEdit(inv.id); setShow(true) }}>✏️</Button>
              <Button size="sm" variant="danger" onClick={() => del(inv.id, inv.description)}>🗑️</Button>
            </div>
          </div>
        )
      })}

      {!filteredInvoices.length && (
        <div className="empty-state">
          <div className="empty-icon">💳</div>
          <p>{filterStatus !== 'all' || filterProject !== 'all' ? 'Tidak ada invoice yang cocok' : 'Belum ada invoice'}</p>
        </div>
      )}

      <Modal show={show} onClose={() => setShow(false)} title={edit ? '✏️ Edit Invoice' : '📄 Buat Invoice'}
        footer={<><Button variant="secondary" onClick={() => setShow(false)}>Batal</Button><Button onClick={save}>💾 Simpan</Button></>}
      >
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Proyek</label>
            <select className="form-select" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
              <option value="">Pilih Proyek</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">No. Termin</label>
            <input className="form-input" type="number" min="1" value={form.term_number} onChange={e => setForm({ ...form, term_number: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Deskripsi *</label>
          <input className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Cth: Termin 1 - Pekerjaan Pondasi" />
        </div>
        <div className="form-group">
          <label className="form-label">Nilai</label>
          <MoneyInput value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tanggal</label>
            <input className="form-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Jatuh Tempo</label>
            <input className="form-input" type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="draft">Draft</option>
            <option value="sent">Terkirim</option>
            <option value="paid">Lunas</option>
            <option value="overdue">Jatuh Tempo</option>
          </select>
        </div>
      </Modal>
    </div>
  )
}